import React, {useState, useEffect, useRef} from 'react';
import styled from 'styled-components';
import {FormattedMessage} from 'react-intl';
import moment from 'moment';
import {IFilters} from '../hooks/useAnalytics';
import IntlStore from "stories/IntlStore";
import {useAnalyticsFilters} from "./hooks/useAnalyticsFilters";
import {useArtists} from "./hooks/useArtists";
import {usePlatforms} from "./hooks/usePlatforms";
import {useCountries} from "./hooks/useCountries";
import {useLabels} from "./hooks/useLabels";
import {useTracks} from "./hooks/useTracks";
import {useReleases} from "./hooks/useReleases";
import {FilterDropdown} from "./dropdown/FilterDropdown";
import {FILTER_TYPES, getFilterConfig} from "components/Analytics/Filters/utils/filterConfig";
import {findMatchingPreset} from "components/DateRangeDropdown/utils/datePresets";
import {ANALYTICS_TRIGGER_STYLE, DateRangeTrigger} from "components/DateRangeDropdown/components/DateRangeTrigger";
import DateRangeDropdown from "components/DateRangeDropdown";
import {ANALYTICS_STYLE} from "components/DateRangeDropdown/styles";

interface FiltersProps {
    filterArtistIds: string[];
    filterPlatformIds: string[];
    filterTerritoryIds: string[];
    filterLabelIds: string[];
    filterReleaseIds: string[];
    filterTrackIds: string[];
    filterDate: Date[];
    onSelectFilter: (filter: IFilters) => void;
    onResetFilters: () => void;
}

interface IDataItem {
    id: string | number;
    name?: string;
    title?: string;
}

type FilterType = typeof FILTER_TYPES[keyof typeof FILTER_TYPES];

const PLACEHOLDER_TEXT = "Select a period";

export const Filters: React.FC<FiltersProps> = (props) => {
    const {
        filterArtistIds, filterPlatformIds, filterTerritoryIds, filterLabelIds,
        filterReleaseIds, filterDate, filterTrackIds,
        onSelectFilter, onResetFilters,
    } = props;

    const {filters} = useAnalyticsFilters();
    const {artists} = useArtists();
    const {platforms} = usePlatforms();
    const {labels} = useLabels();
    const {countries} = useCountries();
    const {tracks} = useTracks();
    const {releases} = useReleases();

    const [open, setOpen] = useState(false);
    const [selectedData, setSelectedData] = useState<string>(PLACEHOLDER_TEXT);
    const periodRangeRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (filterDate && filterDate.length === 2) {
            const matchingPreset = findMatchingPreset(filterDate[0], filterDate[1]);

            if (matchingPreset) {
                setSelectedData(matchingPreset);
            } else {
                const from = moment(filterDate[0]).format('YYYY-MM-DD');
                const to = moment(filterDate[1]).format('YYYY-MM-DD');
                setSelectedData(`${from} - ${to}`);
            }
        } else if (!filterDate || filterDate.length === 0) {
            setSelectedData(PLACEHOLDER_TEXT);
        }
    }, [filterDate]);

    const filterConfigs: Array<{
        type: FilterType;
        data: IDataItem[];
        value: string[];
    }> = [
        {type: FILTER_TYPES.PLATFORM, data: filters?.platforms || platforms, value: filterPlatformIds},
        {type: FILTER_TYPES.TERRITORY, data: filters?.countries || countries, value: filterTerritoryIds},
        {type: FILTER_TYPES.LABEL, data: filters?.labels || labels, value: filterLabelIds},
        {type: FILTER_TYPES.ARTIST, data: filters?.artists || artists, value: filterArtistIds},
        {
            type: FILTER_TYPES.RELEASE,
            data: (filters?.releases || releases).filter(r => r.title),
            value: filterReleaseIds
        },
        {
            type: FILTER_TYPES.TRACK,
            data: (filters?.tracks || tracks).filter(t => t.title),
            value: filterTrackIds
        }
    ];

    const renderFilter = (type: FilterType, data: IDataItem[], value: string[]) => {
        const config = getFilterConfig(type);

        return (
            <FilterDropdown
                key={type}
                value={value}
                options={data.map(item => ({
                    label: item.name || item.title || '',
                    value: String(item.id)
                }))}
                placeholder={IntlStore.intl.formatMessage({
                    id: config.placeholderId,
                    defaultMessage: config.defaultPlaceholder
                })}
                onChange={(val) => onSelectFilter({[config.filterKey]: val} as IFilters)}
                filterPlaceholder={config.filterPlaceholder}
            />
        );
    };

    const handleDateRangeChange = (dates: Date[]) => {
        onSelectFilter({filterDate: dates} as IFilters);
    };

    const handleReset = () => {
        onResetFilters();
        setSelectedData(PLACEHOLDER_TEXT);
    };

    const handleClearDateRange = () => {
        onSelectFilter({filterDate: []} as IFilters);
        setSelectedData(PLACEHOLDER_TEXT);
    };

    return (
        <Container>
            <FiltersGrid>
                {filterConfigs.map(({type, data, value}) =>
                    renderFilter(type, data, value)
                )}

                <DateFilterWrapper>
                    <DateRangeTrigger
                        selectedData={selectedData}
                        isOpen={open}
                        onClick={() => setOpen(!open)}
                        onClear={handleClearDateRange}
                        triggerRef={periodRangeRef}
                        styleConfig={ANALYTICS_TRIGGER_STYLE}
                        placeholderText={PLACEHOLDER_TEXT}
                    />
                    <DateRangeDropdown
                        open={open}
                        setOpen={setOpen}
                        setNewData={handleDateRangeChange}
                        selectedData={selectedData}
                        setSelectedData={setSelectedData}
                        periodRangeRef={periodRangeRef}
                        styleConfig={ANALYTICS_STYLE}
                    />
                </DateFilterWrapper>

                <ResetButton onClick={handleReset}>
                    <FormattedMessage
                        id="Analytics.Filters.Reset"
                        defaultMessage="Reset"
                    />
                </ResetButton>
            </FiltersGrid>
        </Container>
    );
};

const Container = styled.div`
    width: 100%;
    margin: 15px 0;
`;

const FiltersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, max-content));
    gap: 6px;
    align-items: center;

    @media (max-width: 1200px) {
        grid-template-columns: repeat(4, 1fr);
    }

    @media (max-width: 992px) {
        grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

const DateFilterWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;

    @media (max-width: 760px) {
        flex-direction: column;
    }
`;

const ResetButton = styled.div<{ $isDisabled?: boolean }>`
    cursor: ${props => props.$isDisabled ? 'default' : 'pointer'};
    color: #00000080;

    @media (max-width: 480px) {
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;